<template>
  <header
    id="cmp-header"
    class="fixed top-0 left-0 right-0 z-10 bg-white border-b-2 border-gray-200"
  >
    <nav class="h-20 px-8 flex justify-between items-center">
      <RouterLink
        class="text-gray-700 no-underline font-bold text-2xl sm:text-3xl"
        to="/"
      >
        SF Content Management Portal
      </RouterLink>

      <LoadingSpinner v-if="loadingUser" />
      <div v-else-if="user" class="relative flex gap-4 items-center">
        <div
          v-if="isMirusUser"
          class="rounded-lg p-1 border-2 border-dashed border-orange-400 bg-orange-50 text-orange-900"
        >
          <label for="vendor" class="block text-orange-900">
            Viewing data for:
          </label>
          <select
            v-model="selectedVendor"
            name="vendor"
            aria-label="vendor"
            class="w-full peer bg-orange-50 border-orange-200 text-orange-900 text-sm"
            @change="handleVendorChange"
          >
            <option value="">All vendors</option>
            <option
              v-for="vendor in vendorOptions.content"
              :key="vendor.key"
              :value="vendor.key"
            >
              {{ vendor.label }}
            </option>
          </select>
        </div>
        <button
          if="user-menu-button"
          ref="userButton"
          class="bg-transparent hover:bg-gray-100 text-gray-700 transition-colors"
          aria-haspopup="true"
          :aria-expanded="dropdownOpen"
          aria-controls="user-menu"
          aria-label="User menu"
          @click="dropdownOpen = !dropdownOpen"
        >
          <UserCircle class="w-8 h-8 text-gray-400" />
        </button>
        <div
          v-if="dropdownOpen"
          if="user-menu"
          ref="dropdown"
          role="menu"
          aria-labelledby="user-menu-button"
          class="absolute top-14 right-0 border-2 border-gray-300 bg-gray-50 p-2 rounded-xl shadow-xl space-y-2"
        >
          <div
            class="flex gap-2.5 border-2 px-3 py-2 border-gray-200 rounded-lg bg-white"
          >
            <UserCircle class="w-10 h-10 text-gray-400" />
            <div class="flex flex-col justify-center">
              <div class="font-semibold">{{ user.email }}</div>
              <div v-if="vendor" class="text-xs font-medium">
                {{ vendor }}
              </div>
            </div>
          </div>
          <RouterLink
            to="/logout"
            role="menuitem"
            class="flex gap-2 items-center w-full px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors no-underline text-gray-700 font-bold"
          >
            <Logout class="w-4 h-4" />
            Log Out
          </RouterLink>
        </div>
      </div>
    </nav>
  </header>
</template>

<script setup>
import { onClickOutside, onKeyStroke, useLocalStorage } from "@vueuse/core";
import { storeToRefs } from "pinia";
import { nextTick, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import Logout from "@/components/icons/Logout.vue";
import UserCircle from "@/components/icons/UserCircle.vue";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import useUserStore from "@/stores/user";

const userStore = useUserStore();
const currentRoute = useRoute();
const router = useRouter();

const { user, isMirusUser, loadingUser, vendor, vendorOptions } =
  storeToRefs(userStore);

const dropdownOpen = ref(false);
const dropdown = ref(null);
const userButton = ref(null);
const selectedVendor = useLocalStorage("vendor");

onClickOutside(
  dropdown,
  () => {
    dropdownOpen.value = false;
  },
  { ignore: [userButton] },
);

onKeyStroke("Escape", () => {
  if (dropdownOpen.value) {
    dropdownOpen.value = false;
    userButton.value?.focus();
  }
});

async function handleVendorChange() {
  await router.push({
    name: currentRoute.name,
    params: {
      ...currentRoute.params,
      vendor: selectedVendor.value || "all",
    },
  });
  router.go();
}

watch(user, async (_user) => {
  if (userStore.isMirusUser) {
    if (!selectedVendor.value) {
      selectedVendor.value = "";
    }
    userStore.ensureVendorOptions();
  }
});
</script>
